/**
 * ProfilePage — User profile management panel (Variant B).
 *
 * Layout:
 *  - Left card:  avatar upload, display name, member since, quick stats
 *  - Right card: tabbed panel — "Profile" (edit info), "Security" (change password),
 *                "Privacy" (subscription / GDPR data / account deletion)
 *
 * Wave 3 refactor: every form field uses `<TextField>` from `components/ui`;
 * surfaces use `<Card>`, CTAs use `<Button>`. The delete-account flow uses
 * the refactored `<ConfirmModal>` (Modal-backed).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { User as UserGlyph, Mail, Lock, Calendar, MapPin } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useIsPremium } from '../stores/premiumStore';
import { changePassword, downloadMyData, deleteMyAccount, fetchMyCertificates, resetStats, resetProgress } from '../api/auth';
import { fetchUserStats } from '../api/auth';
import { AppShell } from '../components/ui';
import Avatar from '../components/Avatar';
import ConfirmModal from '../components/ConfirmModal';
import { Button, Card, Chip, LinkButton, TextField, FieldError } from '../components/ui';

import { downloadCertificate } from '../api/sessions';
import { hasUpcomingExam } from '../utils/examCountdown';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'security' | 'privacy';
type Toast = { type: 'success' | 'error'; message: string } | null;

// ── Password strength ─────────────────────────────────────────────────────────

type StrengthKey = 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong';

function getPasswordStrength(password: string): { score: number; labelKey: StrengthKey | ''; color: string } {
  if (!password) return { score: 0, labelKey: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const levels: { score: number; labelKey: StrengthKey; color: string }[] = [
    { score: 1, labelKey: 'veryWeak', color: 'bg-red-500' },
    { score: 2, labelKey: 'weak', color: 'bg-orange-500' },
    { score: 3, labelKey: 'fair', color: 'bg-yellow-500' },
    { score: 4, labelKey: 'strong', color: 'bg-blue-500' },
    { score: 5, labelKey: 'veryStrong', color: 'bg-green-500' },
  ];
  return levels[Math.min(score - 1, 4)] ?? { score: 0, labelKey: '', color: '' };
}

// ── Inline SVG icons ──────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

// ── Toast component ───────────────────────────────────────────────────────────

function ToastNotification({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  if (!toast) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl px-5 py-3.5 shadow-2xl text-white text-sm font-medium
        transition-all duration-300 animate-in slide-in-from-bottom-4
        ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}
    >
      {toast.type === 'success' ? <CheckIcon /> : <XIcon />}
      {toast.message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100" aria-label={t('account.profile.toast.close')}>
        <XIcon />
      </button>
    </div>
  );
}

// ── Profile Tab ───────────────────────────────────────────────────────────────

function ProfileTab({
  onSave,
}: {
  onSave: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuthStore();
  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  // Seed the exam date empty when the stored value is already in the past: past
  // dates can't be re-sent (server 422s) and can't be cleared, so we nudge the
  // user to schedule the next one. This seed is also the dirty-tracking baseline.
  const initialExamDate = user?.exam_date && hasUpcomingExam(user.exam_date, new Date()) ? user.exam_date : '';
  const initialExamCenter = user?.exam_center ?? '';
  const [examDate, setExamDate] = useState(initialExamDate);
  const [examCenter, setExamCenter] = useState(initialExamCenter);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // `min` for the date picker — today, in local calendar terms, so past dates
  // can't be selected (mirrors the server-side past-date rejection).
  const todayIso = (() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload: {
        display_name: string | null;
        username: string;
        email: string;
        bio: string | null;
        exam_date?: string;
        exam_center?: string;
      } = {
        display_name: displayName || null,
        username,
        email,
        bio: bio || null,
      };
      // Dirty-tracking: only send exam fields when changed to a non-empty value.
      // Omitted key = unchanged (server keeps existing); never send null (server
      // ignores it — can't clear) nor re-send an unchanged past date (would 422).
      if (examDate && examDate !== initialExamDate) payload.exam_date = examDate;
      if (examCenter && examCenter !== initialExamCenter) payload.exam_center = examCenter;
      await updateProfile(payload);
      onSave(t('account.profile.form.successUpdate'));
    } catch (err: unknown) {
      // A 422 returns FastAPI's `detail` as a *list* of error objects, not a
      // string — only surface it when it's actually a string, else a generic
      // localized message (never pass a non-string to <FieldError>).
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('account.profile.form.errorUpdate'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <TextField
        leftIcon={UserGlyph}
        label={t('account.profile.form.displayName')}
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder={t('account.profile.form.displayNamePlaceholder')}
        maxLength={120}
      />

      <TextField
        leftIcon={UserGlyph}
        label={`${t('account.profile.form.username')} *`}
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
        minLength={3}
        maxLength={80}
      />

      <TextField
        leftIcon={Mail}
        label={`${t('account.profile.form.email')} *`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      {/* Bio uses a native <textarea> styled inline — TextField is single-line only.
          Architect approved this exception (no multi-line variant in `ui/`). */}
      <div>
        <label
          htmlFor="profile-bio"
          className="mb-1.5 block text-[12.5px] font-semibold text-slate-700 dark:text-slate-300"
        >
          {t('account.profile.form.bio')}
        </label>
        <textarea
          id="profile-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder={t('account.profile.form.bioPlaceholder')}
          maxLength={500}
          rows={3}
          className="w-full rounded-[11px] border border-slate-200 bg-white px-3.5 py-3 text-[14px] text-slate-900 placeholder-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 resize-none dark:border-white/10 dark:bg-card-dark/80 dark:text-white dark:placeholder-slate-500"
        />
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 text-right">{t('account.profile.form.bioCounter', { count: bio.length })}</p>
      </div>

      <TextField
        leftIcon={Calendar}
        label={t('account.profile.form.examDate')}
        type="date"
        value={examDate}
        onChange={(e) => setExamDate(e.target.value)}
        min={todayIso}
      />

      <TextField
        leftIcon={MapPin}
        label={t('account.profile.form.examCenter')}
        type="text"
        value={examCenter}
        onChange={(e) => setExamCenter(e.target.value)}
        placeholder={t('account.profile.form.examCenterPlaceholder')}
        maxLength={120}
      />

      {error && <FieldError message={error} />}

      <Button type="submit" variant="primary" className="w-full" disabled={saving}>
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t('account.profile.form.saving')}
          </span>
        ) : (
          t('account.profile.form.saveChanges')
        )}
      </Button>
    </form>
  );
}

// ── Security Tab ──────────────────────────────────────────────────────────────

function SecurityTab({ onSave }: { onSave: (msg: string) => void }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = !!newPassword && !!confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = !!confirmPassword && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('account.profile.security.passwordsDontMatch'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSave(t('account.profile.security.successChange'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('account.profile.security.errorChange');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
        <strong>{t('account.profile.security.noticeLabel')}</strong> {t('account.profile.security.notice')}
      </div>

      <TextField
        leftIcon={Lock}
        label={`${t('account.profile.security.currentPassword')} *`}
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        required
        autoComplete="current-password"
      />

      <div>
        <TextField
          leftIcon={Lock}
          label={`${t('account.profile.security.newPassword')} *`}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
          autoComplete="new-password"
        />
        {newPassword && (
          <div className="mt-2 space-y-1">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    i <= strength.score ? strength.color : 'bg-slate-200 dark:bg-white/10'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{strength.labelKey ? t(`account.profile.security.strength.${strength.labelKey}` as const) : ''}</p>
          </div>
        )}
      </div>

      <div>
        <TextField
          leftIcon={Lock}
          label={`${t('account.profile.security.confirmPassword')} *`}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
          error={passwordsMismatch ? t('account.profile.security.passwordsMismatch') : undefined}
          rightSlot={
            passwordsMatch ? (
              <span className="text-green-500" aria-hidden="true">
                <CheckIcon />
              </span>
            ) : passwordsMismatch ? (
              <span className="text-red-500" aria-hidden="true">
                <XIcon />
              </span>
            ) : undefined
          }
        />
      </div>

      {error && <FieldError message={error} />}

      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={saving || passwordsMismatch || !currentPassword || !newPassword}
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t('account.profile.security.saving')}
          </span>
        ) : (
          t('account.profile.security.changePassword')
        )}
      </Button>
    </form>
  );
}

// ── Privacy / Data Tab ────────────────────────────────────────────────────────

function PrivacyTab({ onSave }: { onSave: (msg: string) => void }) {
  const { t } = useTranslation();
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [downloadingData, setDownloadingData] = useState(false);
  const [showDeletePanel, setShowDeletePanel] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Non-account-deleting purges (session history / learning advancement)
  const [purgeConfirm, setPurgeConfirm] = useState<null | 'sessions' | 'progress'>(null);
  const [purging, setPurging] = useState(false);

  const performPurge = async () => {
    const kind = purgeConfirm;
    if (!kind) return;
    setPurging(true);
    try {
      if (kind === 'sessions') await resetStats();
      else await resetProgress();
      await queryClient.invalidateQueries();
      onSave(t(`account.profile.privacy.${kind === 'sessions' ? 'purgeSessionsSuccess' : 'purgeProgressSuccess'}`));
    } catch {
      onSave(t('account.profile.privacy.purgeError'));
    } finally {
      setPurging(false);
      setPurgeConfirm(null);
    }
  };

  const handleDownloadData = async () => {
    setDownloadingData(true);
    try {
      const blob = await downloadMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data.json';
      a.click();
      URL.revokeObjectURL(url);
      onSave(t('account.profile.privacy.downloadSuccess'));
    } catch {
      onSave(t('account.profile.privacy.downloadError'));
    } finally {
      setDownloadingData(false);
    }
  };

  const performDelete = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteMyAccount(deletePassword);
      logout();
      navigate('/login');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('account.profile.privacy.deleteError');
      setDeleteError(msg);
      setShowFinalConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!deletePassword) return;
    setShowFinalConfirm(true);
  };

  const isPremium = useIsPremium();

  return (
    <div className="space-y-6">
      {/* Premium subscription status — amber-toned card */}
      <Card
        variant="subtle"
        className="border-amber-200 bg-gradient-to-r from-amber-50/60 to-orange-50/40 p-5 dark:border-amber-500/20 dark:from-amber-500/5 dark:to-orange-500/5"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              {t('account.profile.privacy.premiumTitle')}
              {isPremium && (
                <Chip tone="amber">{t('account.profile.privacy.premiumActive')}</Chip>
              )}
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {isPremium
                ? t('account.profile.privacy.premiumDescriptionActive')
                : t('account.profile.privacy.premiumDescriptionInactive')}
            </p>
          </div>
          <LinkButton
            to="/pricing"
            variant={isPremium ? 'ghost' : 'amber'}
            className="flex-shrink-0"
          >
            {isPremium ? t('account.profile.privacy.manage') : t('account.profile.privacy.upgrade')}
          </LinkButton>
        </div>
      </Card>

      {/* Download my data */}
      <Card variant="subtle" className="p-5 space-y-3">
        <h3 className="font-semibold text-slate-900 dark:text-white">{t('account.profile.privacy.downloadTitle')}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('account.profile.privacy.downloadDescription')}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDownloadData}
          disabled={downloadingData}
        >
          {downloadingData ? t('account.profile.privacy.downloading') : t('account.profile.privacy.downloadButton')}
        </Button>
      </Card>

      {/* Reset zone — purge session history / learning advancement (non-account-deleting) */}
      <Card
        variant="subtle"
        className="p-5 space-y-3 border-amber-200 dark:border-amber-500/30"
      >
        <h3 className="font-semibold text-amber-600 dark:text-amber-400">{t('account.profile.privacy.resetTitle')}</h3>
        <div className="space-y-3">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('account.profile.privacy.purgeSessionsDescription')}</p>
            <Button type="button" variant="amber" size="sm" disabled={purging}
              onClick={() => setPurgeConfirm('sessions')}>
              {t('account.profile.privacy.purgeSessionsButton')}
            </Button>
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('account.profile.privacy.purgeProgressDescription')}</p>
            <Button type="button" variant="amber" size="sm" disabled={purging}
              onClick={() => setPurgeConfirm('progress')}>
              {t('account.profile.privacy.purgeProgressButton')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Delete account — danger zone (red border preserved) */}
      <Card
        variant="subtle"
        className="p-5 space-y-3 border-red-200 dark:border-red-500/30"
      >
        <h3 className="font-semibold text-red-600 dark:text-red-400">{t('account.profile.privacy.deleteTitle')}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('account.profile.privacy.deleteDescription')}
        </p>

        {!showDeletePanel ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => setShowDeletePanel(true)}
          >
            {t('account.profile.privacy.deleteButton')}
          </Button>
        ) : (
          <form
            onSubmit={handleDeleteSubmit}
            className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-500/10"
          >
            <p className="text-sm font-medium text-red-700 dark:text-red-300">
              {t('account.profile.privacy.deleteConfirmTitle')}
            </p>
            <TextField
              leftIcon={Lock}
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              required
              placeholder={t('account.profile.privacy.deletePasswordPlaceholder')}
              autoComplete="current-password"
            />
            {deleteError && <FieldError message={deleteError} />}
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="danger"
                size="sm"
                disabled={deleting || !deletePassword}
              >
                {deleting ? t('account.profile.privacy.deleting') : t('account.profile.privacy.deleteFinal')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setShowDeletePanel(false); setDeletePassword(''); setDeleteError(''); }}
              >
                {t('account.profile.privacy.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Card>

      <p className="text-xs text-slate-400 dark:text-slate-500">
        {t('account.profile.privacy.privacyNoticeStart')}{' '}
        <Link to="/privacy" className="text-blue-600 hover:underline dark:text-blue-400">{t('account.profile.privacy.privacyNoticeLink')}</Link>
        {' '}{t('account.profile.privacy.privacyNoticeEnd')}
      </p>

      {/* Final destructive confirmation — uses the refactored ConfirmModal (Modal-backed). */}
      {showFinalConfirm && (
        <ConfirmModal
          title={t('account.profile.privacy.deleteConfirmTitle')}
          message={t('account.profile.privacy.deleteDescription')}
          confirmLabel={t('account.profile.privacy.deleteFinal')}
          cancelLabel={t('account.profile.privacy.cancel')}
          variant="danger"
          onConfirm={performDelete}
          onCancel={() => setShowFinalConfirm(false)}
        />
      )}

      {/* Purge confirmation (sessions / advancement) */}
      {purgeConfirm && (
        <ConfirmModal
          title={t(`account.profile.privacy.${purgeConfirm === 'sessions' ? 'purgeSessionsConfirmTitle' : 'purgeProgressConfirmTitle'}`)}
          message={t(`account.profile.privacy.${purgeConfirm === 'sessions' ? 'purgeSessionsConfirmMessage' : 'purgeProgressConfirmMessage'}`)}
          confirmLabel={t('account.profile.privacy.purgeConfirmLabel')}
          cancelLabel={t('account.profile.privacy.cancel')}
          variant="warning"
          onConfirm={performPurge}
          onCancel={() => setPurgeConfirm(null)}
        />
      )}
    </div>
  );
}

// ── Certificates Section ─────────────────────────────────────────────────────

function CertificatesSection() {
  const { t } = useTranslation();
  const { data: certificates, isLoading } = useQuery({
    queryKey: ['my-certificates'],
    queryFn: fetchMyCertificates,
    staleTime: 60_000,
  });

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (sessionId: number) => {
    setDownloadingId(sessionId);
    try {
      const blob = await downloadCertificate(sessionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${sessionId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Ignore
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{t('account.profile.certificates.title')}</h3>
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
        </div>
      </Card>
    );
  }

  if (!certificates || certificates.length === 0) {
    return (
      <Card className="p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{t('account.profile.certificates.title')}</h3>
        <p className="py-2 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('account.profile.certificates.empty')}
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{t('account.profile.certificates.title')}</h3>
      <div className="space-y-2">
        {certificates.map((cert) => (
          <Card
            key={cert.session_id}
            variant="subtle"
            className="flex items-center justify-between p-3"
          >
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {cert.score_percent}% — {new Date(cert.started_at).toLocaleDateString()}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('account.profile.certificates.session', { id: cert.session_id })}</p>
            </div>
            <button
              type="button"
              onClick={() => handleDownload(cert.session_id)}
              disabled={downloadingId === cert.session_id}
              className="text-sm text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
            >
              {downloadingId === cert.session_id ? t('account.profile.certificates.downloading') : t('account.profile.certificates.download')}
            </button>
          </Card>
        ))}
      </div>
    </Card>
  );
}

// ── Shield Icon ──────────────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}



// ── Main ProfilePage ──────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, uploadAvatar } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [toast, setToast] = useState<Toast>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: fetchUserStats,
  });

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  }, []);

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', t('account.profile.avatar.errorImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('error', t('account.profile.avatar.errorSize'));
      return;
    }
    setAvatarUploading(true);
    try {
      await uploadAvatar(file);
      showToast('success', t('account.profile.avatar.success'));
    } catch {
      showToast('error', t('account.profile.avatar.errorUpload'));
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const displayName = user?.display_name || user?.username || t('account.profile.defaultUserName');

  const statCards = [
    { label: t('account.profile.stats.questions'), value: stats?.total_answered ?? '—', icon: '📝' },
    { label: t('account.profile.stats.score'), value: stats ? `${stats.overall_score}%` : '—', icon: '🎯' },
    { label: t('account.profile.stats.exams'), value: stats?.exam_count ?? '—', icon: '📋' },
    { label: t('account.profile.stats.streak'), value: stats ? `${stats.current_streak}d` : '—', icon: '🔥' },
  ];

  return (
    <AppShell maxWidth="7xl">
      <div className="relative z-10 mx-auto w-full max-w-5xl">
        {/* Page header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-3 flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            {t('account.profile.back')}
          </button>
          <h1 className="text-2xl font-extrabold tracking-[-0.03em] text-slate-900 dark:text-white">
            {t('account.profile.pageTitleStart')}{' '}
            <span className="text-blue-600 dark:text-blue-400">
              {t('account.profile.pageTitleHighlight')}
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('account.profile.pageSubtitle')}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

          {/* ── Left panel: Avatar + info ─────────────────────────────── */}
          <div className="space-y-5 lg:col-span-1">

            {/* Avatar card */}
            <Card className="relative flex flex-col items-center gap-4 overflow-hidden p-8">
              <div className="relative rounded-3xl ring-4 ring-white/10">
                <Avatar
                  src={user?.avatar_url}
                  name={displayName}
                  size="lg"
                  onClick={handleAvatarClick}
                />
                {avatarUploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                    <svg className="h-7 w-7 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />

              <div className="text-center">
                <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{displayName}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">@{user?.username}</p>
                {user?.bio && (
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{user.bio}</p>
                )}
              </div>

              <Button
                variant="ghost"
                className="w-full"
                onClick={handleAvatarClick}
                disabled={avatarUploading}
              >
                {avatarUploading ? t('account.profile.uploading') : `📸 ${t('account.profile.changePhoto')}`}
              </Button>
            </Card>

            {/* Stats strip */}
            <Card className="p-6">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{t('account.profile.yourStats')}</h3>
              <div className="grid grid-cols-2 gap-3">
                {statCards.map(({ label, value, icon }) => (
                  <Card
                    key={label}
                    variant="subtle"
                    className="flex flex-col items-center gap-1 p-3"
                  >
                    <span className="text-xl">{icon}</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-slate-100">{value}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
                  </Card>
                ))}
              </div>
            </Card>

            {/* Email info */}
            <Card className="space-y-2 p-5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">{t('account.profile.emailLabel')}</span>
                <span className="max-w-[150px] truncate font-medium text-slate-800 dark:text-slate-200">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">{t('account.profile.statusLabel')}</span>
                <Chip tone="green">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500" />
                  {t('account.profile.active')}
                </Chip>
              </div>
            </Card>

            {/* Certificates */}
            <CertificatesSection />
          </div>

          {/* ── Right panel: Tabs ─────────────────────────────────────── */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">

              {/* Tab bar */}
              <div className="flex overflow-x-auto border-b border-slate-200 dark:border-white/10">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`-mb-px flex flex-shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-4 text-sm font-medium transition-colors sm:px-6
                    ${activeTab === 'profile'
                      ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                >
                  <UserIcon />
                  {t('account.profile.tabs.profile')}
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`-mb-px flex flex-shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-4 text-sm font-medium transition-colors sm:px-6
                    ${activeTab === 'security'
                      ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                >
                  <LockIcon />
                  {t('account.profile.tabs.security')}
                </button>
                <button
                  onClick={() => setActiveTab('privacy')}
                  className={`-mb-px flex flex-shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-4 text-sm font-medium transition-colors sm:px-6
                    ${activeTab === 'privacy'
                      ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                >
                  <ShieldIcon />
                  {t('account.profile.tabs.privacy')}
                </button>
              </div>

              {/* Tab content */}
              <div className="p-6">
                {activeTab === 'profile' && (
                  <ProfileTab onSave={(msg) => showToast('success', msg)} />
                )}
                {activeTab === 'security' && (
                  <SecurityTab onSave={(msg) => showToast('success', msg)} />
                )}
                {activeTab === 'privacy' && (
                  <PrivacyTab onSave={(msg) => showToast('success', msg)} />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ToastNotification toast={toast} onClose={() => setToast(null)} />
    </AppShell>
  );
}
